import apiClient from './axios.config';
import {
  DepotInput,
  DepotSummary,
  DepotUserAssignment,
  TenantInput,
  TenantProfile,
  UserAccessProfile,
  UserAccount,
  UserInput,
} from '../models/userManagement';

const mapTenantResponse = (payload: any): TenantProfile => ({
  id: payload?.id || payload?.tenantId,
  name: payload?.name || payload?.tenantName || 'Tenant',
  description: payload?.description,
  allowedModelTypes: payload?.allowedModelTypes || payload?.modelTypes || [],
  defaultModelType: payload?.defaultModelType || payload?.modelType || '',
  active: payload?.active ?? true,
  createdAt: payload?.createdAt,
  updatedAt: payload?.updatedAt,
  userCount: payload?.userCount,
  depotCount: payload?.depotCount,
});

const mapUserResponse = (payload: any): UserAccount => ({
  email: payload?.email,
  name: payload?.name,
  phone: payload?.phone,
  roles: payload?.roles || [],
  tenantId: payload?.tenantId,
  active: payload?.active ?? true,
  lastLoginAt: payload?.lastLoginAt,
  createdAt: payload?.createdAt,
});

const mapDepotResponse = (payload: any): DepotSummary => ({
  depotId: payload?.depotId || payload?.id,
  name: payload?.name || payload?.depotName || 'Depot',
  tenantId: payload?.tenantId,
  modelType: payload?.modelType,
  active: payload?.active ?? true,
  city: payload?.city,
  region: payload?.region,
  assignedUserCount: payload?.assignedUserCount,
  updatedAt: payload?.updatedAt,
  configFilesMap: payload?.configFilesMap ?? {},
});

const extractErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

const buildApiError = (error: unknown, fallback: string): Error =>
  new Error(extractErrorMessage(error, fallback));

export const userManagementApi = {
  async getCurrentUserProfile(): Promise<UserAccessProfile> {
    try {
      const { data } = await apiClient.get('/v1/users/me');
      return {
        email: data.email,
        name: data.name,
        avatarUrl: data.avatarUrl,
        tenantId: data.tenantId,
        tenantName: data.tenantName,
        roles: data.roles || [],
        assignedDepots: (data.assignedDepots || []).map(mapDepotResponse),
      };
    } catch (error) {
      throw buildApiError(error, 'Unable to load profile');
    }
  },

  async listTenants(): Promise<TenantProfile[]> {
    try {
      const { data } = await apiClient.get('/v1/tenants');
      const items = Array.isArray(data) ? data : data.items || [];
      return items.map(mapTenantResponse);
    } catch (error) {
      throw buildApiError(error, 'Unable to load tenants');
    }
  },

  async getTenant(tenantId: string): Promise<TenantProfile> {
    try {
      const { data } = await apiClient.get(`/v1/tenants/${tenantId}`);
      return mapTenantResponse(data);
    } catch (error) {
      throw buildApiError(error, 'Unable to load tenant');
    }
  },

  async createTenant(payload: TenantInput): Promise<TenantProfile> {
    try {
      const { data } = await apiClient.post('/v1/tenants', payload);
      return mapTenantResponse(data);
    } catch (error) {
      throw buildApiError(error, 'Failed to create tenant');
    }
  },

  async updateTenant(id: string, payload: Partial<TenantInput>): Promise<TenantProfile> {
    try {
      const { data } = await apiClient.put(`/v1/tenants/${id}`, payload);
      return mapTenantResponse(data);
    } catch (error) {
      throw buildApiError(error, 'Failed to update tenant');
    }
  },

  async listUsers(): Promise<UserAccount[]> {
    try {
      const { data } = await apiClient.get('/v1/users');
      const items = Array.isArray(data) ? data : data.items || [];
      return items.map(mapUserResponse);
    } catch (error) {
      throw buildApiError(error, 'Unable to load users');
    }
  },

  async createUser(payload: UserInput): Promise<UserAccount> {
    try {
      const { data } = await apiClient.post('/v1/users', payload);
      return mapUserResponse(data);
    } catch (error) {
      throw buildApiError(error, 'Failed to create user');
    }
  },

  async updateUser(email: string, payload: Partial<UserInput>): Promise<UserAccount> {
    try {
      const { data } = await apiClient.put(`/v1/users/${encodeURIComponent(email)}`, payload);
      return mapUserResponse(data);
    } catch (error) {
      throw buildApiError(error, 'Failed to update user');
    }
  },

  async listDepots(): Promise<DepotSummary[]> {
    try {
      const { data } = await apiClient.get('/v1/users/me/depots');
      const items = Array.isArray(data) ? data : data.items || [];
      return items.map(mapDepotResponse);
    } catch (error) {
      throw buildApiError(error, 'Unable to load depots');
    }
  },

  async createDepot(payload: DepotInput): Promise<DepotSummary> {
    try {
      const requestBody = {
        id: payload.depotId,
        name: payload.name,
        tenantId: payload.tenantId,
        modelType: payload.modelType,
        active: payload.active ?? true,
        configFilesMap: payload.configFilesMap ?? {},
      };
      const { data } = await apiClient.post('/v1/depots', requestBody);
      return mapDepotResponse(data);
    } catch (error) {
      throw buildApiError(error, 'Failed to create depot');
    }
  },

  async updateDepot(depotId: string, payload: Partial<DepotInput>): Promise<DepotSummary> {
    try {
      const requestBody: Record<string, unknown> = {};
      if (payload.name !== undefined) requestBody.name = payload.name;
      if (payload.modelType !== undefined) requestBody.modelType = payload.modelType;
      if (payload.active !== undefined) requestBody.active = payload.active;
      if (payload.configFilesMap !== undefined) requestBody.configFilesMap = payload.configFilesMap;
      const { data } = await apiClient.put(`/v1/depots/${depotId}`, requestBody);
      return mapDepotResponse(data);
    } catch (error) {
      throw buildApiError(error, 'Failed to update depot');
    }
  },

  async listDepotUsers(depotId: string): Promise<DepotUserAssignment[]> {
    try {
      const { data } = await apiClient.get(`/v1/depots/${depotId}/users`);
      const items = Array.isArray(data) ? data : data.items || [];
      return items.map((item: any) => ({
        depotId,
        email: item.email || item.userEmail,
        assignedAt: item.assignedAt,
      }));
    } catch (error) {
      throw buildApiError(error, 'Unable to load depot users');
    }
  },

  async assignDepotUser(depotId: string, email: string): Promise<void> {
    try {
      await apiClient.post(`/v1/depots/${depotId}/users`, { email });
    } catch (error) {
      throw buildApiError(error, 'Failed to assign user');
    }
  },

  async removeDepotUser(depotId: string, email: string): Promise<void> {
    try {
      await apiClient.delete(`/v1/depots/${depotId}/users/${encodeURIComponent(email)}`);
    } catch (error) {
      throw buildApiError(error, 'Failed to remove user');
    }
  },
};
